import type { ReactNode } from 'react';

export interface SegmentedOption<T extends string> {
    value: T;
    label: ReactNode;
    // Accessible name when the label is an icon or abbreviation.
    ariaLabel?: string;
    className?: string;
}

interface SegmentedProps<T extends string> {
    options: ReadonlyArray<SegmentedOption<T>>;
    value: T;
    onChange(value: T): void;
    // Names the group for assistive technology.
    label: string;
    size?: 'md' | 'sm';
    className?: string;
}

// A filled switch between a few mutually exclusive choices.
export function Segmented<T extends string>({ options, value, onChange, label, size = 'md', className }: SegmentedProps<T>) {
    const classes = ['seg', `seg-${size}`, className].filter(Boolean).join(' ');
    return (
        <div role="group" aria-label={label} className={classes}>
            {options.map(option => (
                <button
                    key={option.value}
                    type="button"
                    aria-pressed={option.value === value}
                    aria-label={option.ariaLabel}
                    className={[option.className, option.value === value ? 'on' : undefined].filter(Boolean).join(' ') || undefined}
                    onClick={() => onChange(option.value)}
                >
                    {option.label}
                </button>
            ))}
        </div>
    );
}
